
import React from 'react';
import { X } from 'lucide-react';
import { useTheme, FeatureColor } from '../contexts/ThemeContext';

interface BaseModalProps {
  isOpen?: boolean; // Optional if conditionally rendered by parent
  onClose: () => void;
  title: string;
  icon?: React.ReactNode;
  headerColor?: FeatureColor; // Override default theme modal header
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string; // Tailwind max-w class
}

export const BaseModal: React.FC<BaseModalProps> = ({ 
    onClose, 
    title, 
    icon, 
    headerColor, 
    children, 
    footer,
    maxWidth = 'max-w-4xl'
}) => {
    const { theme } = useTheme();

    return (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in ${theme.components.modal.overlay}`}>
            <div className={`w-full ${maxWidth} max-h-[90vh] overflow-hidden flex flex-col relative animate-scale-up ${theme.components.modal.container}`}>
                
                {/* Header */}
                <div className={`p-6 flex justify-between items-start shrink-0 ${
                    headerColor 
                    ? `${headerColor.bg} ${headerColor.text}`
                    : theme.components.modal.header
                }`}>
                    <div>
                        {icon && (
                             <div className={`flex items-center gap-2 opacity-80 mb-1 text-sm font-medium uppercase tracking-wider`}>
                                {icon}
                             </div>
                        )}
                        <h2 className={`text-2xl font-bold ${headerColor ? '' : theme.colors.text.main}`}>{title}</h2>
                    </div>
                    <button 
                        onClick={onClose} 
                        className={`p-2 rounded-full transition-colors ${
                            headerColor 
                            ? 'bg-white/20 hover:bg-white/30 text-white' 
                            : `${theme.colors.text.secondary} hover:bg-gray-100 dark:hover:bg-slate-800`
                        }`}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className={`flex-1 overflow-y-auto custom-scrollbar ${theme.components.modal.body}`}>
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className={`p-4 ${theme.components.modal.footer}`}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};
